"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getResourceActiveWidgetPanel = exports.getResourceWidgetPanels = exports.getResourceWidgetPanelStatus = exports.getResourcePermission = exports.getResourceNetworking = exports.getResourceRevision = exports.getResourceCollaborator = exports.getResourcePack = void 0;
const types_1 = require("../../../../../types");
const utils_1 = require("../../../../../utils");
const collaborators_1 = require("../collaborators");
const dashboard_1 = require("./dashboard");
const datasheet_1 = require("./datasheet");
const widget_1 = require("./widget");
const form_1 = require("./form");
const mirror_1 = require("../../../../../modules/database/store/selectors/resource/mirror");
__exportStar(require("./mirror"), exports);
__exportStar(require("./dashboard"), exports);
__exportStar(require("./datasheet"), exports);
__exportStar(require("./form"), exports);
__exportStar(require("./widget"), exports);
__exportStar(require("./theme"), exports);
// pass right `type`, can auto complete type,  for example:
// const s = getResourcePack(undefined as any, undefined as any, ResourceType.Widget);
// `s` can be judge as `IDatasheetPack`
// TODO: still not find out how fix the error of function return. set them `as any`.
// in the future, we can use judge the right return type.
function getResourcePack(state, id, type) {
    switch (type) {
        case types_1.ResourceType.Widget: {
            return state.widgetMap[id];
        }
        case types_1.ResourceType.Form: {
            return state.formMap[id];
        }
        case types_1.ResourceType.Dashboard: {
            return state.dashboardMap[id];
        }
        case types_1.ResourceType.Datasheet: {
            return state.datasheetMap[id];
        }
        case types_1.ResourceType.Mirror: {
            return state.mirrorMap[id];
        }
    }
    return null;
}
exports.getResourcePack = getResourcePack;
const getResourceCollaborator = (state, resourceId, resourceType) => {
    var _a, _b;
    switch (resourceType) {
        case types_1.ResourceType.Form: {
            return (_a = (0, form_1.getFormClient)(state)) === null || _a === void 0 ? void 0 : _a.collaborators;
        }
        case types_1.ResourceType.Dashboard: {
            return (_b = (0, dashboard_1.getDashboardClient)(state)) === null || _b === void 0 ? void 0 : _b.collaborators;
        }
        case types_1.ResourceType.Datasheet: {
            return (0, collaborators_1.collaboratorSocketSelector)(state, resourceId);
        }
        case types_1.ResourceType.Mirror: {
            return (0, mirror_1.getMirrorCollaborator)(state, resourceId);
        }
    }
    return null;
};
exports.getResourceCollaborator = getResourceCollaborator;
const getResourceRevision = (state, id, type) => {
    switch (type) {
        case types_1.ResourceType.Datasheet: {
            const datasheet = (0, datasheet_1.getDatasheet)(state, id);
            return datasheet === null || datasheet === void 0 ? void 0 : datasheet.revision;
        }
        case types_1.ResourceType.Widget: {
            const widget = (0, widget_1.getWidget)(state, id);
            return widget === null || widget === void 0 ? void 0 : widget.revision;
        }
        case types_1.ResourceType.Form: {
            const form = (0, form_1.getForm)(state, id);
            return form === null || form === void 0 ? void 0 : form.revision;
        }
        case types_1.ResourceType.Dashboard: {
            const dashboard = (0, dashboard_1.getDashboard)(state, id);
            return dashboard === null || dashboard === void 0 ? void 0 : dashboard.revision;
        }
        case types_1.ResourceType.Mirror: {
            const mirror = (0, mirror_1.getMirror)(state, id);
            return mirror === null || mirror === void 0 ? void 0 : mirror.revision;
        }
    }
    (0, utils_1.assertNever)(type);
};
exports.getResourceRevision = getResourceRevision;
const getResourceNetworking = (state, resourceId, resourceType) => {
    if (!resourceId) {
        return;
    }
    const resourcePack = getResourcePack(state, resourceId, resourceType);
    if (!resourcePack) {
        return;
    }
    return {
        loading: resourcePack.loading,
        connected: resourcePack.connected,
        syncing: resourcePack.syncing,
        saveStatus: resourcePack.saveStatus,
    };
};
exports.getResourceNetworking = getResourceNetworking;
const getResourcePermission = (state, resourceId, resourceType) => {
    switch (resourceType) {
        case types_1.ResourceType.Datasheet: {
            return (0, datasheet_1.getPermissions)(state, resourceId);
        }
        case types_1.ResourceType.Form: {
            return (0, form_1.getFormPermission)(state, resourceId);
        }
        case types_1.ResourceType.Dashboard: {
            return (0, dashboard_1.getDashboardPermission)(state);
        }
        case types_1.ResourceType.Mirror: {
            return (0, mirror_1.getMirrorPermission)(state, resourceId);
        }
        default: {
            return (0, datasheet_1.getPermissions)(state, resourceId);
        }
    }
};
exports.getResourcePermission = getResourcePermission;
const getResourceWidgetPanelStatus = (state, resourceId, resourceType) => {
    switch (resourceType) {
        case types_1.ResourceType.Datasheet: {
            return (0, datasheet_1.getWidgetPanelStatus)(state, resourceId);
        }
        case types_1.ResourceType.Mirror: {
            return (0, mirror_1.getWidgetPanelStatusWithMirror)(state, resourceId);
        }
        default: {
            return (0, datasheet_1.getWidgetPanelStatus)(state, resourceId);
        }
    }
};
exports.getResourceWidgetPanelStatus = getResourceWidgetPanelStatus;
const getResourceWidgetPanels = (state, resourceId, resourceType) => {
    switch (resourceType) {
        case types_1.ResourceType.Datasheet: {
            return (0, datasheet_1.getWidgetPanels)(state, resourceId);
        }
        case types_1.ResourceType.Mirror: {
            return (0, mirror_1.getWidgetPanelsWithMirror)(state, resourceId);
        }
        default: {
            return (0, datasheet_1.getWidgetPanels)(state, resourceId);
        }
    }
};
exports.getResourceWidgetPanels = getResourceWidgetPanels;
const getResourceActiveWidgetPanel = (state, resourceId, resourceType) => {
    const panels = (0, exports.getResourceWidgetPanels)(state, resourceId, resourceType);
    if (!panels) {
        return panels;
    }
    const widgetPanelStatus = (0, exports.getResourceWidgetPanelStatus)(state, resourceId, resourceType);
    const activePanelId = widgetPanelStatus === null || widgetPanelStatus === void 0 ? void 0 : widgetPanelStatus.activePanelId;
    const panelOpening = widgetPanelStatus === null || widgetPanelStatus === void 0 ? void 0 : widgetPanelStatus.opening;
    if (!panelOpening) {
        return;
    }
    if (activePanelId) {
        return panels.find(item => item.id === activePanelId) || panels[0];
    }
    return panels[0];
};
exports.getResourceActiveWidgetPanel = getResourceActiveWidgetPanel;
//# sourceMappingURL=index.js.map